// Module state for Modal's OverlayHistory (JAR-1412). It lives here, not in
// Modal.tsx, so the test setup can reset it between tests (src/test/setup.ts),
// the way toastStore does for toasts.

// The overlay whose history entry is being taken off right now: set as it
// closes, cleared once its pop has run or been handed on. An overlay that opens
// as another closes (edit popup → confirm) takes that entry over instead of
// stacking a second one behind a pending pop.
let closingOverlay: string | null = null;
const pendingPops = new Set<ReturnType<typeof setTimeout>>();
// Bumped by every reset, so a pop scheduled before a reset can't run after it,
// whatever the timer does with a cleared timeout (a fake one may still call it).
let generation = 0;

export const getClosingOverlay = () => closingOverlay;
export const setClosingOverlay = (id: string | null) => { closingOverlay = id; };

/** Run `pop` on the next tick, unless a reset cancels it first. */
export function schedulePop(pop: () => void): void {
  const made = generation;
  const t = setTimeout(() => { pendingPops.delete(t); if (made === generation) pop(); }, 0);
  pendingPops.add(t);
}

// Overlay entries left behind. An overlay that closes while something sits on
// top of its entry (a second overlay closed by the same action, or a page the
// action navigated to) can't take the entry off: history only pops the top. A
// Back that lands on one would seem to do nothing, so a POP onto a left-behind
// entry keeps going the way it was going. Keyed by the router's per-entry key.
// A fresh load starts with none, so the first render never bounces anyone.
const deadEntries = new Set<string>();
// Only the latest are kept. A session leaves far fewer behind, and an entry that
// far back has almost always been cut from history by a later push.
export const MAX_DEAD_ENTRIES = 50;
let lastIdx = 0;
let listening = false;
// The re-stamp for an overlay's own pops while they're under way (startHousekeeping),
// and the index the next of those pops should land on. A POP that lands anywhere
// else is some other navigation and ends it, so a stale re-stamp never touches an
// unrelated entry.
let housekeeping: { restamp: (landed: Record<string, unknown>) => void; expect: number } | null = null;

function onPopState(e: PopStateEvent): void {
  const st = e.state as { key?: unknown; idx?: unknown; usr?: unknown } | null;
  if (typeof st?.idx !== 'number') return;
  const from = lastIdx;
  lastIdx = st.idx;
  const dead = typeof st.key === 'string' && deadEntries.has(st.key);
  if (housekeeping) {
    if (st.idx === housekeeping.expect) {
      housekeeping.restamp((st.usr ?? {}) as Record<string, unknown>);
      housekeeping = dead ? { ...housekeeping, expect: st.idx - 1 } : null;
    } else {
      housekeeping = null;
    }
  }
  if (!dead) return;
  const back = st.idx < from;
  // Never step back from the first entry: that would leave the app.
  if (!back || st.idx > 0) window.history.go(back ? -1 : 1);
}

function listen(currentIdx: number): void {
  lastIdx = currentIdx;
  if (!listening) { window.addEventListener('popstate', onPopState); listening = true; }
}

/** An overlay entry left behind, and the index history stands at now. */
export function markEntryDead(key: string, currentIdx: number): void {
  deadEntries.add(key);
  if (deadEntries.size > MAX_DEAD_ENTRIES) deadEntries.delete(deadEntries.values().next().value as string);
  listen(currentIdx);
}

/**
 * An overlay is about to pop its own entry off. While it was open, the screen
 * may have written its state onto that entry (the Dashboard stamps its tab
 * there), but the entry the pop lands on still holds the screen's state from
 * before: "Scan in Jarvis" closed two sheets, switched to Jarvis, and landed back
 * on Trip Plan. `restamp` puts the screen's latest state onto each entry the
 * pops land on, until they come to rest.
 */
export function startHousekeeping(restamp: (landed: Record<string, unknown>) => void, currentIdx: number): void {
  housekeeping = { restamp, expect: currentIdx - 1 };
  listen(currentIdx);
}

/**
 * Tests: forget the closing overlay and cancel its pending pop. React's useId
 * restarts with every test's root, so a pop left over from one test could
 * otherwise match, and take off, an overlay entry in the next.
 */
export function __resetOverlayHistory(): void {
  closingOverlay = null;
  pendingPops.forEach(clearTimeout);
  pendingPops.clear();
  deadEntries.clear();
  lastIdx = 0;
  housekeeping = null;
  generation += 1;
  if (listening) { window.removeEventListener('popstate', onPopState); listening = false; }
}
